import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "@/lib/framer-motion-shim";
import { X, Calendar, Clock, User, Mail, Phone, FileText, Check, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

const themeStyles = {
  photographer: {
    bg: "bg-zinc-950",
    card: "bg-zinc-900",
    border: "border-zinc-800",
    input: "bg-zinc-800 border-zinc-700 text-white placeholder-zinc-500 focus:border-white",
    button: "bg-white text-black hover:bg-zinc-200",
    buttonSecondary: "bg-zinc-800 text-white hover:bg-zinc-700 border border-zinc-700",
    text: "text-white",
    textMuted: "text-zinc-400",
    accent: "bg-white text-black",
    selected: "bg-white text-black",
    unselected: "bg-zinc-800 text-zinc-300 hover:bg-zinc-700",
  },
  salon: {
    bg: "bg-rose-50/95",
    card: "bg-white",
    border: "border-rose-200",
    input: "bg-rose-50 border-rose-200 text-stone-800 placeholder-stone-400 focus:border-rose-400",
    button: "bg-rose-400 text-white hover:bg-rose-500",
    buttonSecondary: "bg-white text-stone-700 hover:bg-rose-50 border border-rose-200",
    text: "text-stone-800",
    textMuted: "text-stone-500",
    accent: "bg-rose-400 text-white",
    selected: "bg-rose-400 text-white",
    unselected: "bg-white text-stone-600 hover:bg-rose-50 border border-rose-200",
  },
  restaurant: {
    bg: "bg-stone-100/95",
    card: "bg-white",
    border: "border-stone-200",
    input: "bg-white border-stone-300 text-stone-900 placeholder-stone-400 focus:border-orange-500",
    button: "bg-orange-600 text-white hover:bg-orange-700",
    buttonSecondary: "bg-white text-stone-700 hover:bg-stone-50 border border-stone-200",
    text: "text-stone-900",
    textMuted: "text-stone-500",
    accent: "bg-orange-600 text-white",
    selected: "bg-orange-600 text-white",
    unselected: "bg-white text-stone-600 hover:bg-orange-50 border border-stone-200",
  },
  mechanic: {
    bg: "bg-slate-900/95",
    card: "bg-slate-800",
    border: "border-slate-700",
    input: "bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-blue-500",
    button: "bg-blue-600 text-white hover:bg-blue-700",
    buttonSecondary: "bg-slate-700 text-white hover:bg-slate-600 border border-slate-600",
    text: "text-white",
    textMuted: "text-slate-400",
    accent: "bg-blue-600 text-white",
    selected: "bg-blue-600 text-white",
    unselected: "bg-slate-700 text-slate-300 hover:bg-slate-600",
  },
};

const services = {
  photographer: [
    { id: "portrait", name: "Sessão de Retrato", price: "150€", duration: "1h" },
    { id: "wedding", name: "Cobertura de Casamento", price: "1200€", duration: "8h" },
    { id: "corporate", name: "Fotografia Corporativa", price: "400€", duration: "3h" },
    { id: "family", name: "Sessão de Família", price: "200€", duration: "1.5h" },
  ],
  salon: [
    { id: "cut", name: "Corte Feminino", price: "35€", duration: "45min" },
    { id: "color", name: "Coloração Completa", price: "80€", duration: "2h" },
    { id: "mani", name: "Manicure & Pedicure", price: "45€", duration: "1h" },
    { id: "facial", name: "Tratamento Facial", price: "60€", duration: "1h" },
    { id: "brushing", name: "Brushing", price: "25€", duration: "30min" },
  ],
  restaurant: [
    { id: "lunch", name: "Reserva Almoço", price: "Mesa", duration: "12h-15h" },
    { id: "dinner", name: "Reserva Jantar", price: "Mesa", duration: "19h-23h" },
    { id: "tasting", name: "Menu Degustação", price: "65€/pessoa", duration: "" },
    { id: "event", name: "Evento Privado", price: "Sob consulta", duration: "" },
  ],
  mechanic: [
    { id: "revision", name: "Revisão Completa", price: "89€", duration: "2h" },
    { id: "oil", name: "Mudança de Óleo", price: "45€", duration: "30min" },
    { id: "align", name: "Alinhamento e Balanceamento", price: "55€", duration: "1h" },
    { id: "diag", name: "Diagnóstico Electrónico", price: "35€", duration: "30min" },
    { id: "brakes", name: "Travões (Substituição)", price: "120€", duration: "1.5h" },
  ],
};

export default function BookingModal({ isOpen, onClose, demoType }) {
  const [step, setStep] = useState(1);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", notes: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const styles = themeStyles[demoType] || themeStyles.photographer;
  const demoServices = services[demoType] || services.photographer;

  // Generate dates for next 14 days
  const dates = Array.from({ length: 14 }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() + i + 1);
    return date;
  });

  useEffect(() => {
    if (!selectedDate || !demoType) return undefined;

    let active = true;
    const loadSlots = async () => {
      setLoadingSlots(true);
      await new Promise((resolve) => setTimeout(resolve, 200));
      if (!active) return;

      const fallbackSlots = {
        photographer: ["09:00", "10:30", "14:00", "15:30"],
        salon: ["09:00", "10:00", "11:00", "14:00", "15:00", "16:00"],
        restaurant: ["12:00", "12:30", "13:00", "19:30", "20:00", "20:30"],
        mechanic: ["08:00", "09:00", "10:00", "14:00", "15:00"],
      };
      setAvailableSlots(fallbackSlots[demoType] || []);
      setLoadingSlots(false);
    };

    loadSlots();
    return () => {
      active = false;
    };
  }, [selectedDate, demoType]);

  const handleSubmit = async () => {
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error("Por favor preencha todos os campos obrigatórios");
      return;
    }

    setIsSubmitting(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 700));
      setStep(4);
      toast.success("Reserva confirmada com sucesso!");
    } catch (error) {
      console.error("Booking error:", error);
      toast.error("Erro ao criar reserva. Tente novamente.");
    }
    setIsSubmitting(false);
  };

  const resetAndClose = () => {
    setStep(1);
    setSelectedService(null);
    setSelectedDate(null);
    setSelectedTime(null);
    setFormData({ name: "", email: "", phone: "", notes: "" });
    onClose();
  };

  const formatDate = (date) => {
    const days = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
    const months = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
    return {
      day: days[date.getDay()],
      date: date.getDate(),
      month: months[date.getMonth()],
    };
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        onClick={resetAndClose}
      >
        {/* Backdrop */}
        <div className={`absolute inset-0 ${styles.bg} backdrop-blur-sm`} />

        {/* Modal */}
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className={`relative w-full max-w-lg rounded-2xl ${styles.card} shadow-2xl overflow-hidden`}
          data-testid="booking-modal"
        >
          {/* Header */}
          <div className={`flex items-center justify-between p-6 border-b ${styles.border}`}>
            <div>
              <h2 className={`text-xl font-semibold ${styles.text}`}>Fazer Reserva</h2>
              <p className={`text-sm ${styles.textMuted}`}>Passo {step} de 4</p>
            </div>
            <button
              onClick={resetAndClose}
              className={`p-2 rounded-full ${styles.buttonSecondary}`}
              data-testid="booking-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Progress bar */}
          <div className={`h-1 ${styles.bg}`}>
            <motion.div
              className={styles.accent}
              initial={{ width: "0%" }}
              animate={{ width: `${(step / 4) * 100}%` }}
              transition={{ duration: 0.3 }}
              style={{ height: "100%" }}
            />
          </div>

          {/* Content */}
          <div className="p-6">
            <AnimatePresence mode="wait">
              {/* Step 1: Select Service */}
              {step === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <h3 className={`text-lg font-medium ${styles.text} mb-4`}>Selecione o serviço</h3>
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {demoServices.map((service) => (
                      <button
                        key={service.id}
                        onClick={() => setSelectedService(service)}
                        className={`w-full p-4 rounded-xl text-left transition-all duration-200 ${selectedService?.id === service.id ? styles.selected : styles.unselected}`}
                        data-testid={`service-${service.id}`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{service.name}</p>
                            <p className="text-sm opacity-70">{service.duration}</p>
                          </div>
                          <span className="font-semibold">{service.price}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Step 2: Select Date & Time */}
              {step === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <div>
                    <h3 className={`text-lg font-medium ${styles.text} mb-4 flex items-center gap-2`}>
                      <Calendar className="w-5 h-5" /> Selecione a data
                    </h3>
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {dates.map((date, i) => {
                        const formatted = formatDate(date);
                        const isSelected = selectedDate?.toDateString() === date.toDateString();
                        return (
                          <button
                            key={i}
                            onClick={() => { setSelectedDate(date); setSelectedTime(null); }}
                            className={`flex-shrink-0 w-16 p-3 rounded-xl text-center transition-all duration-200 ${isSelected ? styles.selected : styles.unselected}`}
                            data-testid={`date-${i}`}
                          >
                            <p className="text-xs opacity-70">{formatted.day}</p>
                            <p className="text-xl font-bold">{formatted.date}</p>
                            <p className="text-xs">{formatted.month}</p>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {selectedDate && (
                    <div>
                      <h3 className={`text-lg font-medium ${styles.text} mb-4 flex items-center gap-2`}>
                        <Clock className="w-5 h-5" /> Selecione o horário
                      </h3>
                      {loadingSlots ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="w-6 h-6 animate-spin" />
                        </div>
                      ) : (
                        <div className="grid grid-cols-4 gap-2">
                          {availableSlots.map((time) => (
                            <button
                              key={time}
                              onClick={() => setSelectedTime(time)}
                              className={`p-3 rounded-xl font-medium transition-all duration-200 ${selectedTime === time ? styles.selected : styles.unselected}`}
                              data-testid={`time-${time}`}
                            >
                              {time}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>
              )}

              {/* Step 3: Contact Details */}
              {step === 3 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <h3 className={`text-lg font-medium ${styles.text} mb-4`}>Os seus dados</h3>
                  <div className="space-y-4">
                    <div>
                      <label className={`text-sm ${styles.textMuted} mb-1 block`}>Nome *</label>
                      <div className="relative">
                        <User className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${styles.textMuted}`} />
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="O seu nome"
                          className={`w-full pl-10 pr-4 py-3 rounded-xl border ${styles.input} focus:outline-none`}
                          data-testid="booking-name"
                        />
                      </div>
                    </div>
                    <div>
                      <label className={`text-sm ${styles.textMuted} mb-1 block`}>Email *</label>
                      <div className="relative">
                        <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${styles.textMuted}`} />
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="email@exemplo.com"
                          className={`w-full pl-10 pr-4 py-3 rounded-xl border ${styles.input} focus:outline-none`}
                          data-testid="booking-email"
                        />
                      </div>
                    </div>
                    <div>
                      <label className={`text-sm ${styles.textMuted} mb-1 block`}>Telefone *</label>
                      <div className="relative">
                        <Phone className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${styles.textMuted}`} />
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="+351 912 345 678"
                          className={`w-full pl-10 pr-4 py-3 rounded-xl border ${styles.input} focus:outline-none`}
                          data-testid="booking-phone"
                        />
                      </div>
                    </div>
                    <div>
                      <label className={`text-sm ${styles.textMuted} mb-1 block`}>Notas (opcional)</label>
                      <div className="relative">
                        <FileText className={`absolute left-3 top-3 w-5 h-5 ${styles.textMuted}`} />
                        <textarea
                          value={formData.notes}
                          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                          placeholder="Informações adicionais..."
                          rows={3}
                          className={`w-full pl-10 pr-4 py-3 rounded-xl border ${styles.input} focus:outline-none resize-none`}
                          data-testid="booking-notes"
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 4: Confirmation */}
              {step === 4 && (
                <motion.div
                  key="step4"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-8"
                >
                  <div className={`w-16 h-16 rounded-full ${styles.accent} flex items-center justify-center mx-auto mb-6`}>
                    <Check className="w-8 h-8" />
                  </div>
                  <h3 className={`text-2xl font-bold ${styles.text} mb-2`}>Reserva Confirmada!</h3>
                  <p className={`${styles.textMuted} mb-6`}>
                    Enviámos um email de confirmação para {formData.email}
                  </p>
                  <div className={`p-4 rounded-xl ${styles.unselected} text-left`}>
                    <p className="font-medium">{selectedService?.name}</p>
                    <p className="text-sm opacity-70">
                      {selectedDate?.toLocaleDateString('pt-PT')} às {selectedTime}
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div className={`flex justify-between p-6 border-t ${styles.border}`}>
            {step > 1 && step < 4 ? (
              <button
                onClick={() => setStep(step - 1)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full ${styles.buttonSecondary}`}
                data-testid="booking-prev-btn"
              >
                <ChevronLeft className="w-4 h-4" /> Voltar
              </button>
            ) : (
              <div />
            )}

            {step < 3 && (
              <button
                onClick={() => setStep(step + 1)}
                disabled={step === 1 ? !selectedService : !selectedDate || !selectedTime}
                className={`flex items-center gap-2 px-6 py-2 rounded-full ${styles.button} disabled:opacity-50 disabled:cursor-not-allowed`}
                data-testid="booking-next-btn"
              >
                Continuar <ChevronRight className="w-4 h-4" />
              </button>
            )}

            {step === 3 && (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`flex items-center gap-2 px-6 py-2 rounded-full ${styles.button} disabled:opacity-50`}
                data-testid="booking-submit-btn"
              >
                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Confirmar Reserva
              </button>
            )}

            {step === 4 && (
              <button
                onClick={resetAndClose}
                className={`w-full px-6 py-3 rounded-full ${styles.button}`}
                data-testid="booking-done-btn"
              >
                Concluído
              </button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
